import { useState, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import productService from '../services/products.service'
import categoryService from '../../categories/services/category.service'

export const useProducts = () => {
    const [searchText, setSearchText] = useState('')
    const [categoryFilter, setCategoryFilter] = useState<string>('all')
    const [statusFilter, setStatusFilter] = useState<string>('all')

    // Fetch products
    const { 
        data: products = [], 
        isLoading: isProductsLoading, 
        isFetching: isProductsFetching,
        error: productsError,
        refetch: refetchProducts 
    } = useQuery({
        queryKey: ['products'],
        queryFn: () => productService.getProducts(), // Fetch all for now, filter client side or implement server side params
    })

    // Fetch categories for filter
    const {
        data: categories = [],
    } = useQuery({
        queryKey: ['categories'],
        queryFn: categoryService.getCategories,
    })

    const filteredProducts = useMemo(() => {
        return products.filter(product => {
            const matchesSearch = product.name.toLowerCase().includes(searchText.toLowerCase()) || 
                                  product.sku.toLowerCase().includes(searchText.toLowerCase())
            
            // For category, we might check name OR id depending on what filter value provides.
            // If filter provides 'all', pass.
            // If filter provides a category name (like "Cement"), we can check product.category string.
            // Ideally we pass IDs, but ProductsScreen uses names currently.
            // Let's assume passed filter is the ID or Name.
            // The product.category from usage in UI seems to be a name string currently.
            const matchesCategory = categoryFilter === 'all' || product.category === categoryFilter
            
            const matchesStatus = statusFilter === 'all' || product.status === statusFilter

            return matchesSearch && matchesCategory && matchesStatus
        })
    }, [products, searchText, categoryFilter, statusFilter])

    return {
        products: filteredProducts,
        categories,
        searchText,
        setSearchText,
        categoryFilter,
        setCategoryFilter,
        statusFilter,
        setStatusFilter,
        totalProducts: products.length,
        isLoading: isProductsLoading,
        isFetching: isProductsFetching,
        error: productsError,
        refetch: refetchProducts
    }
}
